import React from 'react';
import { Collapse } from 'antd';
import type { CollapseProps } from 'antd';
import type { AdminProductListItemProps, SizeOption } from '../../interfaces';
import AdminProductStockControl from '../AdminProductStockControl';
import {
  getMaterialInventories,
  getMaterialStock,
  hasMaterialDetails,
} from './adminProductListItem.utils';
import AdminProductMaterialLabel from './AdminProductMaterialLabel';
import AdminProductMaterialLoading from './AdminProductMaterialLoading';
import './AdminProductMaterialCollapse.scss';

type AdminProductMaterialCollapseProps = AdminProductListItemProps & {
  filteredSizes: SizeOption[];
  requiresSizes: boolean;
};

const AdminProductMaterialCollapse: React.FC<AdminProductMaterialCollapseProps> = ({
  product,
  savingStockId,
  savingPriceId,
  savingSizesId,
  loadingMaterialDetailsById,
  draftStockById,
  draftPriceById,
  filteredSizes,
  requiresSizes,
  onLoadMaterialDetails,
  onStockStep,
  onDraftStockInput,
  onDraftPriceInput,
  onCommitDraftStock,
  onCommitDraftPrice,
  onCommitMaterialSizes,
  onAvailabilityChange,
  onVariantAvailabilityChange,
  onUploadMaterialImages,
  onDeleteMaterialImage,
}) => {
  const materialItems: CollapseProps['items'] = product.materialPrices.map((material) => {
    const detailsLoaded = hasMaterialDetails(material);
    const materialInventories = getMaterialInventories(product.id, material);
    const activeVariantCount = materialInventories.filter((variant) => variant.isAvailable).length;
    const variantsCount = materialInventories.length || material.sizes?.length || 0;

    return {
      key: material.id,
      label: (
        <AdminProductMaterialLabel
          material={material}
          detailsLoaded={detailsLoaded}
          requiresSizes={requiresSizes}
          materialStock={getMaterialStock(product.id, material, requiresSizes)}
          activeVariantCount={activeVariantCount}
          variantsCount={variantsCount}
        />
      ),
      children: detailsLoaded ? (
        <AdminProductStockControl
          material={material}
          draftStockById={draftStockById}
          draftPrice={draftPriceById[material.id] ?? material.price}
          isSavingStock={Boolean(material.variantInventories?.some((variant) => variant.id === savingStockId))}
          isSavingPrice={savingPriceId === material.id}
          isSavingSizes={savingSizesId === material.id}
          requiresSizes={requiresSizes}
          sizes={filteredSizes}
          productId={product.id}
          onStockStep={onStockStep}
          onDraftStockInput={onDraftStockInput}
          onDraftPriceInput={onDraftPriceInput}
          onCommitDraftStock={onCommitDraftStock}
          onCommitDraftPrice={onCommitDraftPrice}
          onCommitMaterialSizes={onCommitMaterialSizes}
          onAvailabilityChange={onAvailabilityChange}
          onVariantAvailabilityChange={onVariantAvailabilityChange}
          onUploadMaterialImages={onUploadMaterialImages}
          onDeleteMaterialImage={onDeleteMaterialImage}
        />
      ) : (
        <AdminProductMaterialLoading loading={Boolean(loadingMaterialDetailsById[material.id])} />
      ),
    };
  });

  const handleMaterialCollapseChange: CollapseProps['onChange'] = (keys) => {
    const activeKeys = Array.isArray(keys) ? keys : [keys];

    activeKeys
      .filter((key): key is string => typeof key === 'string')
      .forEach((materialPriceId) => {
        void onLoadMaterialDetails(product.id, materialPriceId);
      });
  };

  return (
    <Collapse
      className="admin-product-form__material-collapse"
      ghost
      items={materialItems}
      onChange={handleMaterialCollapseChange}
    />
  );
};

export default AdminProductMaterialCollapse;
